import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { metricsBus } from "@/common/buses";
import { useUserStore } from "@/common/store/userStore";
import { useAgeVerificationToken } from "@/components/app/AppHooks/hooks/useAgeVerificationToken";
import {
  loadAvSignupPromptBannerDismissed,
  setAvSignupPromptBannerDismissed,
} from "@/common/utils/storage";
import { routes } from "@/common/utils/routes";

const BANNER_TEXT = "Sign up to skip age verification next visit";
const BANNER_CTA = "Sign Up";

export const useAvSignupBanner = () => {
  const currentUser = useUserStore((state) => state.currentUser);
  const navigate = useNavigate();

  const [isHidden, setIsHidden] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => loadAvSignupPromptBannerDismissed());
  const impressionSentRef = useRef(false);

  const { token } = useAgeVerificationToken();

  const isAnonymous = currentUser !== undefined && !currentUser?.username;
  const hasAvToken = Boolean(token);

  const shouldShow = isAnonymous
    && hasAvToken
    && !isDismissed
    && !isHidden;

  useEffect(() => {
    if (shouldShow && !impressionSentRef.current) {
      impressionSentRef.current = true;
      metricsBus.emit("registrationBannerImpression");
    }
  }, [shouldShow]);

  const onLinkAction = useCallback((_id: string) => {
    metricsBus.emit("registrationBannerClick");
    navigate(routes.userSignUp(), { state: { source: "av_banner" } });
  }, [navigate]);

  const onCloseInfoBar = useCallback((_id: string) => {
    metricsBus.emit("registrationBannerClosed");
    setAvSignupPromptBannerDismissed(true);
    setIsDismissed(true);
    setIsHidden(true);
  }, []);

  if (!shouldShow) {
    return null;
  }

  return {
    text: BANNER_TEXT,
    ctaText: BANNER_CTA,
    ctaLink: routes.userSignUp(),
    onLinkAction,
    onCloseInfoBar,
  };
};
